// src/SitesForm.js ver 01 Dec 16 2025 3:25 pm - SB118b
// src/SitesForm.js ver 00a Dec 16 2025 11:29 pm - SB118a
// src/SitesForm.js ver 00 Dec 14 2025 1:08 pm - SB118
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom'; // NEW: useLocation
import { useAuth } from './App';
import * as sitesService from './api/sitesService';
import * as employeeService from './api/employeeService'; // To fetch employees for dropdown
import { ApiError } from './api/apiClient';

const initialSiteState = {
    siteName: '',
    siteCategory: 'Restaurant', // Default category
    siteAddress: '',
    TownOrCity: '',
    StateOrProvince: '',
    ZipOrPostCode: '',
    Country: '',
    TelephoneNumber: '',
    siteContactEmployeeId: '',
    siteContactFullName: '',
    Status: 'Active', // Default status
};

// Allowed dropdown options (for frontend consistency)
const siteCategories = ['HeadOffice', 'Warehouse', 'Central Kitchen', 'Restaurant', 'Kiosk'];
const siteStatuses = ['Active', 'Not-Active'];

function SitesForm() {
    // const { tenantId, id: siteId } = useParams();
    const { id: siteId } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // NEW: To check current path for 'details' mode
    const auth = useAuth();

    const [formData, setFormData] = useState(initialSiteState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [siteNameError, setSiteNameError] = useState(null);
    const [employeeContactOptions, setEmployeeContactOptions] = useState([]); // For siteContactFullName dropdown
    const [employeeContactError, setEmployeeContactError] = useState(null);

    // Determine mode: 'add', 'edit', or 'details'
    const isEditing = Boolean(siteId) && !location.pathname.includes('/sites/details/'); // Is editing if siteId exists AND not in details route
    const isShowingDetails = Boolean(siteId) && location.pathname.includes('/sites/details/'); // Is showing details if siteId exists AND in details route
    const isAdding = !isEditing && !isShowingDetails; // Is adding if no siteId and not in details mode

    // Check if user has permission to edit (or add, as it's the same form)
    const canEditSite = auth.userProfile?.TenantProfileEditor === true;

    const tenantId = auth.userProfile?.SubsCoDocId; // re-added ver01. existing in ver 00a
    console.log(`tenantId = ${tenantId}; siteId = ${siteId}`)

    useEffect(() => {
        const fetchData = async () => {
            if (!tenantId) {
                setError("Tenant ID missing in URL.");
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            setEmployeeContactError(null);

            try {
                const token = await auth.getIdToken();
                if (!token) {
                    setError("Authentication required. Please log in.");
                    setLoading(false);
                    return;
                }

                // Fetch active regular employees for the dropdown
                const employeesResponse = await employeeService.getAllActiveRegularEmployeesForDropdown(tenantId, token);
            //    const employeesResponse = await sitesService.getAllActiveRegularEmployeesForDropdown(tenantId, token);
               console.log(`employeesResponse = ${employeesResponse}; employeesResponse.employees = ${employeesResponse.employees}`);
                if (employeesResponse && employeesResponse.employees) {
                    setEmployeeContactOptions(employeesResponse.employees.map(emp => ({
                        fullName: emp.fullName,
                        employeeId: emp.employeeId,
                    })));
                } else {
                    setEmployeeContactError("Failed to load active employees for contact dropdown.");
                }

                if (isEditing || isShowingDetails) { // Fetch data if editing or showing details
                    const data = await sitesService.getSiteById(tenantId, siteId, token);
                    if (data) {
                        setFormData(data);
                    } else {
                        setError("Site not found.");
                        navigate(`/sites/${tenantId}`);
                    }
                } else { // For adding new site, pre-select default employee if options exist
                    if (employeesResponse && employeesResponse.employees.length > 0) {
                        const defaultEmployee = employeesResponse.employees[0];
                        setFormData(prev => ({
                            ...prev,
                            siteContactFullName: defaultEmployee.fullName,
                            siteContactEmployeeId: defaultEmployee.employeeId,
                        }));
                    }
                }
            } catch (err) {
                console.error("Error fetching data for site form:", err);
                setError(`Failed to load data: ${err.message}`);
                setEmployeeContactError(`Failed to load employees: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, siteId, isEditing, isShowingDetails, navigate, auth]); // Added isShowingDetails to dependencies

    const handleChange = (e) => {
        // Only allow changes if in add or edit mode AND has permission
        if (isShowingDetails || !canEditSite) return;

        const { name, value } = e.target;
        
        if (name === 'siteContactFullName') {
            // Find the selected employee to get their EmployeeId
            const selectedEmployee = employeeContactOptions.find(opt => opt.fullName === value);
            setFormData(prev => ({
                ...prev,
                [name]: value,
                siteContactEmployeeId: selectedEmployee ? selectedEmployee.employeeId : '',
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSiteNameError(null);
        setEmployeeContactError(null);

        // Do not proceed if in details mode OR user does not have edit permission
        if (isShowingDetails || !canEditSite) {
            setError("You do not have permission to perform this action.");
            return;
        }

        if (!tenantId) {
            setError("Tenant ID missing. Cannot save site.");
            return;
        }
        if (!formData.siteName || formData.siteName.trim() === '') {
            setSiteNameError("Site Name is required.");
            return;
        }
        if (!formData.siteCategory || !siteCategories.includes(formData.siteCategory)) {
            setError("Site Category is required and must be a valid option.");
            return;
        }
        if (!formData.Status || !siteStatuses.includes(formData.Status)) {
            setError("Status is required and must be a valid option.");
            return;
        }
        if (!formData.siteAddress || formData.siteAddress.trim() === '') {
            setError("Site Address is required.");
            return;
        }
        if (!formData.TownOrCity || formData.TownOrCity.trim() === '') {
            setError("Town or City is required.");
            return;
        }
        if (!formData.StateOrProvince || formData.StateOrProvince.trim() === '') {
            setError("State or Province is required.");
            return;
        }
        if (!formData.ZipOrPostCode || formData.ZipOrPostCode.trim() === '') {
            setError("Zip or Post Code is required.");
            return;
        }
        if (!formData.Country || formData.Country.trim() === '') {
            setError("Country is required.");
            return;
        }
        if (!formData.TelephoneNumber || formData.TelephoneNumber.trim() === '') {
            setError("Telephone Number is required.");
            return;
        }
        if (!formData.siteContactFullName || formData.siteContactFullName.trim() === '' ||
            !formData.siteContactEmployeeId || formData.siteContactEmployeeId.trim() === '') {
            setEmployeeContactError("Site Contact Full Name and Employee ID are required and must be valid.");
            return;
        }
        
        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication required. Please log in.");
                return;
            }

            const payload = { ...formData };

            if (isEditing) {
                await sitesService.updateSite(tenantId, siteId, payload, token);
                alert("Site updated successfully!");
            } else {
                await sitesService.addSite(tenantId, payload, token);
                alert("Site created successfully!");
            }
            navigate(`/sites`);
        } catch (err) {
            console.error("Error saving site via Cloud Function:", err);
            if (err instanceof ApiError && err.status === 409) {
                setSiteNameError(`Error: ${err.message}`);
                setError(null);
            } else {
                setError(`Failed to save site: ${err.message}`);
            }
        }
    };

    if (loading) return <p>Loading site details...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p className="container" style={{ color: 'red' }}>Error: Tenant ID is required to add/edit site.</p>;


    return (
        <div className="container form-container">
            <h2>
                {isAdding && 'Add New Site (Tenant: '}
                {isEditing && `Edit Site (Tenant: `}
                {isShowingDetails && `Site Details (Tenant: `}
                {tenantId})
            </h2>
            {!canEditSite && (isEditing || isAdding || isShowingDetails) && (
                <p style={{ color: 'orange', marginBottom: '1rem' }}>You are viewing this site in read-only mode. Contact an administrator to gain editing permissions.</p>
            )}
            <form onSubmit={handleSubmit}>
                {(isEditing || isShowingDetails) && formData.siteSeqId && (
                    <div className="form-group">
                        <label>Sequential ID</label>
                        <input type="text" name="siteSeqId" value={formData.siteSeqId || ''} disabled />
                    </div>
                )}
                <div className="form-group">
                    <label>Site Name</label>
                    <input
                        type="text"
                        name="siteName"
                        value={formData.siteName}
                        onChange={handleChange}
                        required
                        disabled={isShowingDetails || !canEditSite}
                    />
                    {siteNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{siteNameError}</p>}
                </div>

                <div className="form-group">
                    <label>Category</label>
                    <select name="siteCategory" value={formData.siteCategory} onChange={handleChange} required disabled={isShowingDetails || !canEditSite}>
                        {siteCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Site Address</label>
                    <input type="text" name="siteAddress" value={formData.siteAddress} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>
                <div className="form-group">
                    <label>Town/City</label>
                    <input type="text" name="TownOrCity" value={formData.TownOrCity} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>
                <div className="form-group">
                    <label>State/Province</label>
                    <input type="text" name="StateOrProvince" value={formData.StateOrProvince} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>
                <div className="form-group">
                    <label>Zip/Post Code</label>
                    <input type="text" name="ZipOrPostCode" value={formData.ZipOrPostCode} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>
                <div className="form-group">
                    <label>Country</label>
                    <input type="text" name="Country" value={formData.Country} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>
                <div className="form-group">
                    <label>Telephone Number</label>
                    <input type="tel" name="TelephoneNumber" value={formData.TelephoneNumber} onChange={handleChange} required disabled={isShowingDetails || !canEditSite} />
                </div>

                <div className="form-group">
                    <label>Contact</label>
                    <select
                        name="siteContactFullName"
                        value={formData.siteContactFullName}
                        onChange={handleChange}
                        required
                        disabled={isShowingDetails || !canEditSite || (employeeContactOptions.length === 0 && !employeeContactError && loading)}
                    >
                        <option value="">-- Select an Employee --</option>
                        {employeeContactOptions.map(emp => (
                            <option key={emp.employeeId} value={emp.fullName}>{emp.fullName}</option>
                        ))}
                    </select>
                    {employeeContactError && <p style={{ color: 'red', fontSize: '0.85em' }}>{employeeContactError}</p>}
                </div>

                <div className="form-group">
                    <label>Contact Employee ID</label>
                    <input type="text" name="siteContactEmployeeId" value={formData.siteContactEmployeeId} disabled />
                </div>

                <div className="form-group">
                    <label>Status</label>
                    <select name="Status" value={formData.Status} onChange={handleChange} required disabled={isShowingDetails || !canEditSite}>
                        {siteStatuses.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                </div>

                {(isEditing || isShowingDetails) && formData.DateAdded && (
                    <div className="form-group">
                        <label>Date Added</label>
                        <input type="text" value={new Date(formData.DateAdded._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}
                {(isEditing || isShowingDetails) && formData.DateLastModified && (
                    <div className="form-group">
                        <label>Date Last Modified</label>
                        <input type="text" value={new Date(formData.DateLastModified._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}

                {isShowingDetails && canEditSite && ( // Red Edit button in details mode if user can edit
                    <button type="button" className="danger" onClick={() => navigate(`/sites/edit/${siteId}`)}>
                        Edit
                    </button>
                )}
                {(isAdding || isEditing) && canEditSite && ( // Blue Save button in add/edit mode if user can edit
                    <button type="submit">Save Site</button>
                )}
            </form>
        </div>
    );
}

export default SitesForm;